import type { PlacePredictionDto, PlaceAddressDetailsDto } from '@onboarding/application/dtos';

/**
 * Extracts the first matching address component by type priority.
 */
const extractComponent = (components: google.maps.GeocoderAddressComponent[], types: string[]): string => {
  for (const type of types) {
    const match = components.find((c) => c.types.includes(type));
    if (match) return match.long_name;
  }
  return '';
};

export type PlacesApiMapperType = {
  mapPredictions: (predictions: google.maps.places.AutocompletePrediction[]) => PlacePredictionDto[];
  mapPlaceResultToAddress: (result: google.maps.places.PlaceResult) => PlaceAddressDetailsDto;
};

/**
 * Creates mapper for converting Google Places API responses to application DTOs.
 */
export const createPlacesApiMapper = (): PlacesApiMapperType => {
  const mapPredictions = (predictions: google.maps.places.AutocompletePrediction[]): PlacePredictionDto[] =>
    predictions.map((p) => ({
      placeId: p.place_id,
      description: p.description,
      mainText: p.structured_formatting.main_text,
      secondaryText: p.structured_formatting.secondary_text,
    }));

  const mapPlaceResultToAddress = (result: google.maps.places.PlaceResult): PlaceAddressDetailsDto => {
    const components = result.address_components ?? [];

    return {
      formattedAddress: result.formatted_address ?? '',
      street: extractComponent(components, ['route']),
      extNumber: extractComponent(components, ['street_number']),
      neighborhood: extractComponent(components, ['sublocality_level_1', 'sublocality', 'neighborhood']),
      city: extractComponent(components, ['locality', 'administrative_area_level_2']),
      state: extractComponent(components, ['administrative_area_level_1']),
      zipCode: extractComponent(components, ['postal_code']),
      country: extractComponent(components, ['country']),
    };
  };

  return { mapPredictions, mapPlaceResultToAddress };
};
